import { isIp } from '../giftPartner.utils';

// This client deliberately does not use the application's JWT/localhost axios client.
export const GIFT_PARTNER_TEST_PATH = '/gift-brand/api-integration/test';
export function resolveGiftPartnerTestBaseUrl(configured?: string) {
  try {
    const url = new URL(configured || '');
    if (url.protocol === 'https:' && !url.username && !url.password) {
      return url.href.replace(/\/+$/, '');
    }
  } catch {
    // Local frontend development still tests the deployed demo API.
  }
  return 'https://api.demo.gaonbugo.com';
}
export const giftPartnerTestBaseUrl = resolveGiftPartnerTestBaseUrl(
  process.env.REACT_APP_GIFT_PARTNER_API_BASE_URL || process.env.REACT_APP_BACKEND_DOMAIN,
);

export type PartnerTestOperation = 'me' | 'list' | 'detail' | 'image';
export type PartnerTestInput = {
  baseUrl: string;
  apiKey: string;
  operation: PartnerTestOperation;
  status?: string;
  limit?: string;
  cursor?: string;
  fullCode?: string;
  imageTarget?: string;
};
export type PartnerTestResult = {
  url: string;
  status: number;
  elapsedMs: number;
  body: string;
  nextCursor?: string;
  hasMore: boolean;
  fullCodes: string[];
  emptyList: boolean;
  thanksCardImages: { fullCode: string; url: string }[];
  imageBlob?: Blob;
  ipRestriction?: { requestIp: string | null };
};

export function partnerTestUrl(input: PartnerTestInput) {
  let base: URL;
  try {
    base = new URL(input.baseUrl.trim());
  } catch {
    throw new Error('올바른 HTTPS API 주소를 입력해 주세요.');
  }
  if (
    base.protocol !== 'https:' ||
    base.username ||
    base.password ||
    base.search ||
    base.hash ||
    !['', '/partner/v1'].includes(base.pathname.replace(/\/+$/, ''))
  ) {
    throw new Error(
      'HTTPS API 기본 주소를 입력해 주세요. 예: https://api.demo.gaonbugo.com',
    );
  }
  let path = '/partner/v1/me';
  if (input.operation === 'list') path = '/partner/v1/gift-delivery';
  if (input.operation === 'detail') {
    const code = input.fullCode?.trim();
    if (!code || !/^[a-zA-Z0-9_-]+-\d+$/.test(code)) {
      throw new Error('배송 코드를 입력해 주세요. 예: K3F9QZ7A-2');
    }
    path = `/partner/v1/gift-delivery/${encodeURIComponent(code)}`;
  }
  if (input.operation === 'image') {
    const target = input.imageTarget?.trim() || input.fullCode?.trim() || '';
    if (/^[a-zA-Z0-9_-]+-\d+$/.test(target)) {
      path = `/partner/v1/gift-delivery/${encodeURIComponent(target)}/thanks-card/image`;
    } else {
      let imageUrl: URL;
      try {
        imageUrl = new URL(target);
      } catch {
        throw new Error('감사카드 이미지 URL 또는 배송 코드를 입력해 주세요.');
      }
      if (
        imageUrl.origin !== base.origin ||
        imageUrl.username ||
        imageUrl.password ||
        imageUrl.search ||
        imageUrl.hash ||
        !/^\/partner\/v1\/gift-delivery\/[a-zA-Z0-9_-]+-\d+\/thanks-card\/image$/.test(
          imageUrl.pathname,
        )
      ) {
        throw new Error(
          '선택한 API Base URL과 같은 서버의 감사카드 이미지 URL을 입력해 주세요.',
        );
      }
      path = imageUrl.pathname;
    }
  }
  const url = new URL(path, base.origin);
  if (input.operation === 'list') {
    const limit = Number(input.limit || 10);
    if (!Number.isInteger(limit) || limit < 1 || limit > 100) {
      throw new Error('조회 건수는 1~100 사이 정수로 입력해 주세요.');
    }
    url.searchParams.set('limit', String(limit));
    if (input.status) url.searchParams.set('status', input.status);
    if (input.cursor?.trim()) url.searchParams.set('cursor', input.cursor.trim());
  }
  return url.href;
}

export async function callPartnerTest(
  input: PartnerTestInput,
  signal: AbortSignal,
): Promise<PartnerTestResult> {
  const url = partnerTestUrl(input);
  const apiKey = input.apiKey.trim();
  if (!apiKey) throw new Error('발급받은 API 키를 입력해 주세요.');
  const started = Date.now();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'x-api-key': apiKey,
      Accept: input.operation === 'image' ? 'image/*' : 'application/json',
    },
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
    signal,
  });
  if (input.operation === 'image' && response.ok) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().startsWith('image/')) {
      throw new Error(
        '서버가 이미지가 아닌 응답을 반환했습니다. 이미지 URL을 확인해 주세요.',
      );
    }
    const imageBlob = await response.blob();
    if (!imageBlob.size)
      throw new Error('빈 이미지 응답입니다. 이미지 URL을 확인해 주세요.');
    return {
      url,
      status: response.status,
      elapsedMs: Date.now() - started,
      body: '',
      imageBlob,
      hasMore: false,
      fullCodes: [],
      emptyList: false,
      thanksCardImages: [],
    };
  }
  const raw = await response.text();
  let data;
  let body = raw;
  try {
    data = JSON.parse(raw);
    body = JSON.stringify(data, null, 2);
  } catch {
    // Proxy/HTTP errors may return text or HTML; render them only as plain text.
  }
  const items = response.ok && Array.isArray(data?.items) ? data.items : undefined;
  // Use the IP observed by the API server, not an unrelated IP lookup service.
  const deniedIp =
    response.status === 403 && typeof data?.message === 'string'
      ? /^ip not allowed:\s*(.+)$/i.exec(data.message.trim())?.[1]?.trim()
      : undefined;
  return {
    url,
    status: response.status,
    elapsedMs: Date.now() - started,
    body: body.split(apiKey).join('[API KEY REDACTED]'),
    nextCursor:
      response.ok && typeof data?.nextCursor === 'string' ? data.nextCursor : undefined,
    hasMore: response.ok && data?.hasMore === true,
    fullCodes: (items ?? []).flatMap((item: { fullCode?: unknown } | null) =>
      typeof item?.fullCode === 'string' ? [item.fullCode] : [],
    ),
    emptyList: !!items && items.length === 0,
    thanksCardImages: response.ok
      ? (items ?? [data]).flatMap(
          (item: { fullCode?: unknown; thanksCard?: { imageUrl?: unknown } } | null) =>
            typeof item?.fullCode === 'string' &&
            typeof item?.thanksCard?.imageUrl === 'string' &&
            item.thanksCard.imageUrl
              ? [{ fullCode: item.fullCode, url: item.thanksCard.imageUrl }]
              : [],
        )
      : [],
    ipRestriction: deniedIp ? { requestIp: isIp(deniedIp) ? deniedIp : null } : undefined,
  };
}

export function partnerTestHint(status: number) {
  if (status === 401) return 'API 키, 폐기 여부, 연동 승인 및 활성 상태를 확인해 주세요.';
  if (status === 403)
    return '접근이 거부되었습니다. 서버 응답 메시지와 연동 권한을 확인해 주세요.';
  if (status === 404)
    return '배송 코드와 해당 키의 브랜드 범위를 확인해 주세요. 테스트 상가 주문은 조회에서 제외됩니다.';
  if (status === 429) return '호출 한도를 초과했습니다. 1분 뒤 다시 시도해 주세요.';
  if (status >= 500) return '서버 오류입니다. 응답 내용과 요청 주소를 확인해 주세요.';
  if (status >= 400) return '요청 조건과 응답 메시지를 확인해 주세요.';
  return '요청이 정상 처리되었습니다.';
}
